import type { IScope } from "angular";
import { cloneDeep } from "lodash";
import { catchError, concatMap, finalize, map } from "rxjs/operators";
import type { Observable } from "rxjs";
import { of as observableOf, throwError as observableThrowError } from "rxjs";
import "@progress/kendo-ui/js/kendo.autocomplete";

import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

import type { IAccountRepository } from "App/Modules/Auth";
import type {
    ILabel,
    IPartner,
    IPartnerUserFlat,
    IPartnerUserInsert,
    IPartnerUserSlim,
    IResponse,
    IResponseWithLoading,
    IRole,
} from "App/Modules/Models";
import { RoleFunctions } from "App/Modules/Models";
import type { IPartnerUsersRepository, IRolesRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";

enum AddUserDialogStates {
    SelectPartnerUser,
    DefinePartnerUserParameters,
    DefinePartnerUserRole,
}

export default class AddPartnerUserComponentController {
    public static $inject: string[] = [
        "$scope",
        "mmPartnerUsersRepository",
        "mmAccountRepository",
        "irRolesRepository",
    ];

    public newPartnerUser: IPartnerUserSlim;

    public existingPartnerUser: IPartnerUserSlim;

    public partner: IPartner;

    public withLimitedPermissions: boolean;

    public partnerUsersResponse: IResponseWithLoading<IPartnerUserSlim[]>;

    public existingPartnerUserPartners: string[] = [];

    public isNew: boolean;

    public createLogin: boolean;

    public enableTwoFactorAuth: boolean;

    public state: AddUserDialogStates = AddUserDialogStates.SelectPartnerUser;

    public partnerRoles: IRole[];

    public partnerUserRole: IRole;

    public labels: string[];

    public partnerUserLabelsString: string;

    public resolve: IFormContainerResolve<IPartnerUserSlim, { partner: IPartner; withLimitedPermissions: boolean }>;

    public addResponse: IResponseWithLoading<IPartnerUserFlat>;

    constructor(
        private readonly $scope: IScope,
        private readonly partnerUsersRepository: IPartnerUsersRepository,
        private readonly accountRepository: IAccountRepository,
        private readonly rolesRepository: IRolesRepository,
    ) {}

    public $onInit() {
        this.partner = this.resolve.bindings.partner;
        this.withLimitedPermissions = this.resolve.bindings.withLimitedPermissions;
        this.enableTwoFactorAuth = false;

        const rolesObservable = this.withLimitedPermissions
            ? this.rolesRepository.getRolesForSysAdmin()
            : this.rolesRepository.getPartnerRoles();

        extendWithLoadingAndFail(rolesObservable)
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                if (!response.hasError && response.data) {
                    const roles = this.withLimitedPermissions
                        ? response.data
                        : [this.rolesRepository.getNoRole()].concat(response.data);
                    this.partnerRoles = roles;
                    this.partnerUserRole = roles[0];
                }
            });

        extendWithLoadingAndFail(this.partnerUsersRepository.getLabels(this.partner.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((response) => {
                if (!response.hasError && response.data) {
                    this.labels = response.data.map((l) => l.label);
                }
            });

        extendWithLoadingAndFail(this.partnerUsersRepository.getPartnerUsersWithRole(this.partner.id))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (response) => {
                    if (!response.isLoading && response.data) {
                        const ids = response.data.map((x) => x.id);
                        this.existingPartnerUserPartners = ids;
                    }
                },
                (error) => {
                    this.partnerUsersResponse = error;
                },
            );
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }

    public sortById(role: IRole) {
        return RoleFunctions.sortById(role);
    }

    public selectExistingPartnerUser(partnerUser: IPartnerUserSlim) {
        this.isNew = false;
        this.existingPartnerUser = cloneDeep(partnerUser);
        this.newPartnerUser = partnerUser;
        this.state = AddUserDialogStates.DefinePartnerUserParameters;
    }

    public createNewPartnerUser(searchString: string) {
        this.isNew = true;
        this.newPartnerUser = {} as IPartnerUserSlim;

        const isEmail = /^[\w+.-]+@[\d.A-Za-z-]+$/;
        const isPhoneNumber = /^[\d +.-]+$/;

        if (isEmail.test(searchString)) {
            this.newPartnerUser.email = searchString;
        } else if (isPhoneNumber.test(searchString)) {
            this.newPartnerUser.phoneNumber = searchString;
            this.newPartnerUser.smsNumber = searchString;
        } else {
            this.newPartnerUser.fullName = searchString;
        }
        this.state = AddUserDialogStates.DefinePartnerUserParameters;
    }

    public next() {
        this.state = AddUserDialogStates.DefinePartnerUserRole;
    }

    public add() {
        let userObservable: Observable<IResponse<IPartnerUserSlim>>;
        if (this.isNew) {
            userObservable = this.createUser(this.newPartnerUser);
        } else {
            userObservable = observableOf({
                data: this.newPartnerUser,
            } as IResponse<IPartnerUserSlim>);
        }

        extendWithLoadingAndFail(
            userObservable.pipe(
                concatMap((partnerUserResponse) => {
                    this.newPartnerUser.id = partnerUserResponse.data.id;
                    const partnerUser: IPartnerUserFlat = this.newPartnerUser;
                    partnerUser.role = this.partnerUserRole && this.partnerUserRole.id ? this.partnerUserRole : null;

                    if (this.partnerUserLabelsString) {
                        partnerUser.labels = this.partnerUserLabelsString
                            .split(",")
                            .map((l) => l.trim())
                            .filter((l) => l !== "")
                            .map((l) => ({ label: l } as ILabel));
                    }

                    return this.partnerUsersRepository.patchPartnerUser(
                        this.partner.id,
                        this.newPartnerUser.id,
                        partnerUser,
                    );
                }),
            ),
        ).subscribe(
            (response) => {
                this.addResponse = response;
                if (!response.isLoading && response.data) {
                    this.resolve.options.onOk(response.data as IPartnerUserSlim);
                }
            },
            (error) => {
                this.addResponse = error;
            },
        );
    }

    private createUser(partnerUser: IPartnerUserSlim): Observable<IResponse<IPartnerUserSlim>> {
        const partnerUserInsert = {
            displayName: partnerUser.fullName,
            email: partnerUser.email,
            enableTwoFactorAuth: this.enableTwoFactorAuth,
            fullName: partnerUser.fullName,
            partnerId: this.partner.id,
            phoneNumber: partnerUser.phoneNumber,
            smsNumber: partnerUser.smsNumber,
            userId: partnerUser.id,
        } as IPartnerUserInsert;

        let result = this.partnerUsersRepository.addPartnerUser(this.partner.id, partnerUserInsert).pipe(
            catchError((err: IResponse<void>) => {
                err.error = {
                    // Does not follow typings
                    message: "Failed to create user",
                    serverError: err.error,
                } as any;
                return observableThrowError(err);
            }),
        );

        if (this.createLogin) {
            result = result.pipe(
                concatMap((pu: IResponse<IPartnerUserSlim>) =>
                    this.partnerUsersRepository.verify(this.partner.id, pu.data.id, this.enableTwoFactorAuth).pipe(
                        catchError((err: IResponse<void>) => {
                            err.error = {
                                // Does not follow typings
                                message: "Failed to verify user",
                                serverError: err.error,
                            } as any;
                            return observableThrowError(err);
                        }),
                        map(() => pu),
                    ),
                ),
            );

            if (this.enableTwoFactorAuth) {
                result = result.pipe(
                    concatMap((pu: IResponse<IPartnerUserSlim>) =>
                        this.accountRepository.twoFactorAuthentication(pu.data.email).pipe(
                            catchError((err: IResponse<void>) => {
                                err.error = {
                                    // Does not follow typings
                                    message: "Failed to create user",
                                    serverError: err.error,
                                } as any;
                                return observableThrowError(err);
                            }),
                            map(() => pu),
                        ),
                    ),
                );
            }
        }

        return result;
    }
}
